import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, GitCompareArrows, Layers } from "lucide-react";

/**
 * Model Diff — side-by-side comparison of two composed models.
 * Route: /studio/diff  (pick two)  or  /studio/diff/:a/:b  (direct link).
 */
export default function StudioDiff() {
  const { a, b } = useParams();
  const nav = useNavigate();
  const [models, setModels] = useState({ mine: [], public: [] });
  const [left, setLeft] = useState(a || "");
  const [right, setRight] = useState(b || "");
  const [leftDoc, setLeftDoc] = useState(null);
  const [rightDoc, setRightDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/ai-models").then((r) => setModels(r.data));
  }, []);

  useEffect(() => {
    setErr("");
    if (left && left !== leftDoc?.ai_model_id) {
      api.get(`/ai-models/${left}`).then((r) => setLeftDoc(r.data)).catch(() => setErr("Left model not accessible"));
    } else if (!left) setLeftDoc(null);
    if (right && right !== rightDoc?.ai_model_id) {
      api.get(`/ai-models/${right}`).then((r) => setRightDoc(r.data)).catch(() => setErr("Right model not accessible"));
    } else if (!right) setRightDoc(null);
    // Sync URL
    if (left && right && (left !== a || right !== b)) {
      nav(`/studio/diff/${left}/${right}`, { replace: true });
    }
    // eslint-disable-next-line
  }, [left, right]);

  const options = [...models.mine, ...models.public].filter((m) => m.kind === "composed");

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="back-to-studio">
        <ArrowLeft size={14} strokeWidth={2.5} /> Studio
      </Link>

      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          <GitCompareArrows size={12} strokeWidth={2.5} /> Model Diff
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="diff-title">Spot the differences</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Pick any two composed models to see how their missions, tones, temperatures, and few-shot examples diverge — perfect for iterating.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Picker label="Left model" value={left} onChange={setLeft} options={options} testId="diff-picker-left" />
        <Picker label="Right model" value={right} onChange={setRight} options={options} testId="diff-picker-right" />
      </div>

      {err && <div className="brut-card p-4 text-red-600 text-sm">{err}</div>}

      {leftDoc && rightDoc ? (
        <div className="brut-card p-0 overflow-hidden" data-testid="diff-panel">
          <DiffRow label="Name" left={leftDoc.name} right={rightDoc.name} />
          <DiffRow label="Avatar" left={leftDoc.avatar} right={rightDoc.avatar} big />
          <DiffRow label="Base brain" left={leftDoc.base_brain_id} right={rightDoc.base_brain_id} mono />
          <DiffRow label="Tone" left={leftDoc.tone} right={rightDoc.tone} />
          <DiffRow label="Temperature" left={leftDoc.temperature} right={rightDoc.temperature} mono />
          <DiffRow label="Max tokens" left={leftDoc.max_tokens} right={rightDoc.max_tokens} mono />
          <DiffRow label="Tags" left={(leftDoc.tags || []).join(", ")} right={(rightDoc.tags || []).join(", ")} />
          <DiffRow label="Style guardrail" left={leftDoc.guardrails?.style || "—"} right={rightDoc.guardrails?.style || "—"} />
          <DiffRow label="Format guardrail" left={leftDoc.guardrails?.format || "—"} right={rightDoc.guardrails?.format || "—"} />
          <DiffRow label="Banned words" left={(leftDoc.guardrails?.banned || []).join(", ") || "—"} right={(rightDoc.guardrails?.banned || []).join(", ") || "—"} />
          <DiffRow label="Few-shot count" left={(leftDoc.few_shot || []).length} right={(rightDoc.few_shot || []).length} mono />
          <DiffRow label="System prompt" left={leftDoc.system_prompt} right={rightDoc.system_prompt} pre />
          {(leftDoc.last_grade || rightDoc.last_grade) && (
            <DiffRow label="Latest grade" left={leftDoc.last_grade?.average?.overall ?? "—"} right={rightDoc.last_grade?.average?.overall ?? "—"} mono />
          )}
        </div>
      ) : (
        <div className="brut-card p-8 text-center">
          <div className="text-4xl mb-2"><Layers size={40} className="inline" strokeWidth={2} /></div>
          <div className="font-heading font-bold text-lg mb-1">Pick two composed models</div>
          <p className="text-sm text-slate-600">Their configs will appear here side-by-side with the differences highlighted.</p>
        </div>
      )}
    </div>
  );
}

function Picker({ label, value, onChange, options, testId }) {
  return (
    <div className="brut-card p-4">
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">{label}</div>
      <select
        className="brut-input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      >
        <option value="">— select —</option>
        {options.map((o) => (
          <option key={o.ai_model_id} value={o.ai_model_id}>{o.avatar} {o.name}</option>
        ))}
      </select>
    </div>
  );
}

function DiffRow({ label, left, right, mono, pre, big }) {
  const same = String(left ?? "") === String(right ?? "");
  const bg = same ? "bg-white" : "bg-amber-50";
  return (
    <div className={`grid grid-cols-[140px_1fr_1fr] gap-0 border-b-2 border-slate-100 last:border-b-0`}>
      <div className="p-3 bg-slate-50 border-r-2 border-slate-100 text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 flex items-start">
        {label}{!same && <span className="ml-1 text-amber-600">•</span>}
      </div>
      <div className={`p-3 ${bg} border-r-2 border-slate-100 ${mono ? "font-mono text-xs" : "text-sm"} ${big ? "text-3xl" : ""} ${pre ? "whitespace-pre-wrap" : ""}`}>{String(left ?? "—")}</div>
      <div className={`p-3 ${bg} ${mono ? "font-mono text-xs" : "text-sm"} ${big ? "text-3xl" : ""} ${pre ? "whitespace-pre-wrap" : ""}`}>{String(right ?? "—")}</div>
    </div>
  );
}
